<template>
    <Card>
        <div class="row items-center">
            <div class="col">
                <div v-if="!hasElearningSessions">
                    <ul v-if="sessionGroup.sessions.length > 1"
                        class="grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3">
                        <li v-for="(session, i) in sessionGroup.sessions" class="text-sm font-semibold">
                            {{ session.module.title }} <br>
                            <CalendarFormat :date="session.start" :showState="false"/>
                        </li>
                    </ul>
                    <CalendarFormat
                        v-if="sessionGroup.sessions.length === 1"
                        :date="sessionGroup.sessions[0].start"
                    :showState="false"/>
                </div>
                <div v-else>
                    <p>Available Now</p>
                </div>

                <div class="mt-4">
                    <button @click.prevent="showDetails = true" class="flex items-center text-sm">
                        <font-awesome-icon icon="info-circle" size="sm"/>
                        <span class="ml-1">View location details</span>
                    </button>
                </div>
            </div>
            <div class="col-auto">
               <template v-if="beforeCutoff || hasElearningSessions">
                   <p class="text-sm" v-if="!hasElearningSessions">
                       <strong>{{ availableSpaces }}</strong> Available places left
                   </p>
                   <Button
                       v-if="!authenticated && (beforeCutoff || hasElearningSessions)"
                       class="w-full mt-2 ml-auto"
                       @click.prevent="showLoginModal">
                       Sign in to apply
                   </Button>
                   <SubmitButton
                       v-if="sessionGroup.user_can.apply && availableSpaces > 0"
                       class="w-full mt-2 ml-auto"
                       @click.prevent="apply"
                       :submitting="applying">
                       Apply
                   </SubmitButton>
               </template>
                <SubmitButton
                    v-if="canCancel"
                    class="w-full mt-2 ml-auto"
                    @click.prevent="cancelApplication"
                    :submitting="cancelling">
                    Cancel
                </SubmitButton>
            </div>
        </div>
        <Modal v-model:show="showDetails">
            <div class="relative py-3 px-4">

                <button @click.prevent="showDetails = false" class="absolute h-5 w-5 top-0 right-0 mt-4 mr-4">
                    <svg class="w-full h-full" viewBox="0 0 20 20" fill="currentColor">
                        <path fill-rule="evenodd"
                              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                              clip-rule="evenodd"/>
                    </svg>
                </button>
                <h2 class="text-xl">Details</h2>
                <p v-if="hasZoomSessions" class="text-xs italic text-gray-600">*Zoom links will be provided upon
                    acceptance.
                </p>
                <p v-if="hasElearningSessions" class="text-xs italic text-gray-600">*E-Learning links will be provided upon
                    acceptance.
                </p>
                <ul
                    v-if="sessionGroup.sessions.length"
                    class="mt-4 space-y-4 ">
                    <li v-for="(session, i) in sessionGroup.sessions" class="space-y-1">
                        <h4 v-if="sessionGroup.sessions.length > 1" class="font-semibold ">
                            {{ session.module.title }} <br>
                        </h4>
                        <p class="text-sm">Type - {{ getType(session) }}</p>
                        <p v-if="session.location_notes" class="text-sm">{{ session.location_notes }}</p>
                    </li>
                </ul>
            </div>
        </Modal>
    </Card>
</template>

<script>

import Card from "@/Components/Card";
import CalendarFormat from "@/Components/CalendarFormat";
import Button from "@/Components/Button";
import SubmitButton from "@/Components/Form/SubmitButton";
import axios from "axios";
import {mapGetters} from "vuex";
import notify from "@/Lib/Notify";
import Modal from "@/Components/Modal";
import {CourseSessionType} from "@/Lib/Enums";
import dayjs from "dayjs";


export default {

    name: "CourseApplicationCard",

    props: {
        sessionGroup: {
            type: Object,
            required: true,
        }
    },

    emits: [
        'update'
    ],

    data() {
        return {
            applying: false,
            cancelling: false,
            showDetails: false
        }
    },

    components: {
        Card,
        CalendarFormat,
        Button,
        SubmitButton,
        Modal,
    },

    methods: {
        async apply() {

            this.applying = true;
            try {
                await axios.post(route('api.course-applications.store'), {
                    course_session_group_id: this.sessionGroup.id,
                    user_id: this.user.id
                });

                this.$notify.success("Your application has been sent to the course Trainer. You should soon receive a confirmation email if you have been accepted.");

                this.$emit('update');

            } catch (e) {

                if (e.response.status === 400) {
                    this.$notify.warning(e.response.data.message);
                    this.$emit('update');
                } else {
                    notify.warning({
                        title: 'Something went wrong!',
                        text: 'If the issue persists please flag the issue.',
                    });
                    console.error(e);
                    this.applying = false;
                }
            }
        },
        async cancelApplication() {

            if (!this.canCancel) {
                return;
            }

            if (window.confirm('Are you sure you want to cancel this application?')) {

                this.cancelling = true;

                axios.delete(route('api.course-applications.destroy', this.application), {
                    // Prevent page not found notification as 404 will be returned if the booking does not exist.
                    validateStatus: status => (status >= 200 && status < 300) || status === 404,
                }).then(() => {
                    this.$emit('update')
                    this.$notify.success("You have cancelled your application for this course.");
                }).catch(e => {
                    notify.warning({
                        title: 'Something went wrong!',
                        text: 'If the issue persists please flag the issue.',
                    });
                    console.error(e);
                    this.cancelling = false;
                })
            }
        },
        showLoginModal() {
            document.dispatchEvent(new CustomEvent(`show-login`));
        },
        getType(session) {
            switch (session.type) {
                case CourseSessionType.F2F:
                    return 'Face-to-face';
                case CourseSessionType.Zoom:
                    return 'Zoom';
                case CourseSessionType.Elearning:
                    return 'E-Learning';
            }
        },
    },

    computed: {
        ...mapGetters([
            'authenticated',
            'user',
            'isStudent'
        ]),
        application() {
            if (this.sessionGroup.applications && this.sessionGroup.applications.length) {
                return this.sessionGroup.applications[0]
            }
        },
        availableSpaces() {
            return this.sessionGroup.spaces - this.sessionGroup.accepted_applications_count;
        },
        canCancel() {
            return this.application && this.application.user_can.delete;
        },
        hasZoomSessions() {
            return this.sessionGroup.sessions.filter(item => item.type === CourseSessionType.Zoom).length > 0;
        },
        hasElearningSessions() {
            return this.sessionGroup.sessions.filter(item => item.type === CourseSessionType.Elearning).length > 0;
        },
        beforeCutoff() {
            return this.sessionGroup.cutoff_date && dayjs().isBefore(this.sessionGroup.cutoff_date);
        }
    },

    watch: {
        sessionGroup: {
            deep: true,
            handler() {
                this.applying = false;
                this.cancelling = false;
            },
        }
    }
};

</script>
